import { ComponentType } from '@angular/cdk/portal';
import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { EnpsRouterComponent } from '@common/components/base/enps-router-component';
import { DataStore } from '@common/data-models/data-store/data-store';
import { ViewStateFilterStates } from '@common/enums/viewstate-filter-states';
import { ViewStateFilterVisibility } from '@common/enums/viewstate-filter-visibility';
import { AuthService } from '@common/services/authentication/auth.service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { LoaderComponent } from '@loader/loader';
import { Store } from '@ngrx/store';
import { CastViewStateComponent } from '@src/suite/common/components/view-state/cast-view-state';
import { DiscoverViewStateComponent } from '@src/suite/common/components/view-state/discover-view-state';
import { LiveViewStateComponent } from '@src/suite/common/components/view-state/live-view-state';
import { PlaybookViewStateComponent } from '@src/suite/common/components/view-state/playbook-view-state';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { ViewStateActions, ViewStateApiActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { AppService } from '@suite/app.service';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'st-planning-views',
    templateUrl: './planning-views.html',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-planning-views',
    },
    styleUrls: ['./planning-views.scss'],
})
export class PlanningViewsComponent extends EnpsRouterComponent implements OnInit, AfterContentInit, AfterViewInit {
    public loading: boolean = false;

    @ViewChild('loader') loader: LoaderComponent;
    public loaderLabel: string;
    @ViewChild('mainCta', { read: TemplateRef, static: true }) mainCta;
    @ViewChild('secondaryCta', { read: TemplateRef, static: true }) secondaryCta;
    @ViewChild('viewOptions', { read: TemplateRef, static: true }) viewOptions;
    @ViewChild('rightSlot', { read: TemplateRef, static: true }) rightSlot;

    public viewStateFilterPanelStates = ViewStateFilterStates;
    public viewStateFilterVisibility = ViewStateFilterVisibility;

    get hasViews(): boolean {
        return DataStore.getViewStateResults(this.appService.applicationName)?.views?.length > 0;
    }

    get views(): ViewStateRecord[] {
        return DataStore.getViewStateResults(this.appService.applicationName)?.views;
    }

    get hasSearches(): boolean {
        return DataStore.getViewStateResults(this.appService.applicationName)?.searches?.length > 0;
    }

    get searches(): ViewStateRecord[] {
        return DataStore.getViewStateResults(this.appService.applicationName)?.searches;
    }

    get hasSlideshows(): boolean {
        return DataStore.getViewStateResults(this.appService.applicationName)?.multiviews?.length > 0;
    }

    get slideshows(): ViewStateRecord[] {
        return DataStore.getViewStateResults(this.appService.applicationName)?.multiviews;
    }

    destroyed$: Subject<void> = new Subject<void>();
    private _application: string = null;
    application$ = this.store
        .select(selectCurrentApplication)
        .pipe(
            tap(application => (this._application = application)),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    constructor(
        public appService: AppService,
        private changeDetectorRef: ChangeDetectorRef,
        authService: AuthService,
        activatedRoute: ActivatedRoute,
        public navigationService: NavigationService,
        public languageSettings: LanguageSettings,
        public viewStateService: LayoutState,
        public viewStateDialog: MatDialog,
        private store: Store
    ) {
        super(authService, activatedRoute);

        this.loaderLabel = this.languageSettings.PSTWebGettingListLoaderLabel;
        this.store.dispatch(ViewStateApiActions.loadViewStateRecords({ group: 'all' }));
    }

    async ngOnInit() {
        this.navigationService.currentListView = this;
    }

    ngAfterContentInit(): void {
        this.store.dispatch(ViewStateActions.updateTemplateRefs({ mainCta: this.mainCta, secondaryCta: this.secondaryCta, viewOptions: this.viewOptions, rightSlot: this.rightSlot }));
    }

    public showLoader() {
        this.loading = true;
    }

    public hideLoader() {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.authService.shouldReload) return;
    }

    private getViewState(): ComponentType<PlaybookViewStateComponent | CastViewStateComponent | DiscoverViewStateComponent | LiveViewStateComponent> | null {
        if (this._application === 'playbook') {
            return PlaybookViewStateComponent;
        }
        if (this._application === 'cast') {
            return CastViewStateComponent;
        }
        if (this._application === 'discover') {
            return DiscoverViewStateComponent;
        }
        if (this._application === 'live') {
            return LiveViewStateComponent;
        }
        return null;
    }

    public async createViewState() {
        const viewState = this.getViewState();
        if (viewState !== null) {
            this.viewStateDialog.closeAll();
            await this.viewStateDialog.afterAllClosed.pipe(take(1)).toPromise();

            let width = '35rem';
            let height = '90vh';
            let panelClass = 'st-view-state-dialog';
            if (this._application === 'discover') {
                width = '50rem';
                height = 'auto';
                panelClass = 'discover-view-state-dialog';
            }

            const dialogRef = this.viewStateDialog.open(viewState, {
                width: width,
                height: height,
                hasBackdrop: true,
                panelClass: panelClass,
                data: {
                    copy: false,
                    edit: false,
                    vs: null,
                    group: null,
                    section: null,
                    item: null,
                },
            });
            dialogRef.afterClosed().subscribe((result: boolean) => {
                if (result) {
                    this.viewStateService.getViewStateRecords();
                }
            });
        }
    }
}
